import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Paper,
  Button,
  Group,
  TextInput,
  Alert,
  Loader,
  Table,
  Anchor,
  Collapse,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconExternalLink,
  IconRefresh,
  IconVideo,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { BOB_MODALS } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";

const SponsorshipModal = observer(({ bob }: { bob: Bob }) => {
  const isOpen = bob.openModal === BOB_MODALS.SPONSORSHIP;
  const sponsorship = bob.selectedSponsorship;
  const [invalidOpen, { toggle: toggleInvalid }] = useDisclosure(false);
  const [infoOpen, { toggle: toggleInfo }] = useDisclosure(false);

  useEffect(() => {
    if (isOpen && sponsorship && !sponsorship.selectedVideoProposal) {
      sponsorship.createAndSelectNewVideoProposal();
    }
    if (!isOpen && sponsorship) {
      sponsorship.deleteAllVideoProposals();
    }
  }, [isOpen]);

  if (!isOpen || !sponsorship) return null;

  const videoProposal = sponsorship.selectedVideoProposal;

  return (
    <Modal
      opened={isOpen}
      onClose={() => {
        sponsorship.deleteAllVideoProposals();
        sponsorship.setVideoViewsUpdateSuccess(null);
        sponsorship.setVideoViewsUpdateError(null);
        sponsorship.setViewRedemptionSuccess(null);
        sponsorship.setViewRedemptionError(null);
        sponsorship.setBonusRedemptionSuccess(null);
        sponsorship.setBonusRedemptionError(null);
        bob.setOpenModal(null);
      }}
      closeOnClickOutside={false}
      title={`Sponsorship: ${sponsorship.tokuserName}`}
    >
      <Paper withBorder shadow="md" p={24} radius="md">
        <Stack style={{ wordBreak: "break-word" }}>
          {!sponsorship.startingBonusRedeemedAt && (
            <>
              <Text size="sm" fw={500}>
                Welcome to the Track One Recordings sponsorship!
              </Text>
              <Text size="sm">
                You have a {sponsorship.startingBonusSatoshis.toLocaleString()}{" "}
                sats ({bob.formatFiat(sponsorship.startingBonusSatoshis)})
                starting bonus to redeem.
              </Text>
              <Button
                onClick={() => sponsorship.redeemStartingBonus()}
                loading={bob.areWeWaitingOnRails}
              >
                Redeem Starting Bonus
              </Button>
              {sponsorship.bonusRedemptionError && (
                <Text c="red" size="sm">
                  {sponsorship.bonusRedemptionError}
                </Text>
              )}
              {sponsorship.bonusRedemptionSuccess && (
                <Text c="green" size="sm">
                  {sponsorship.bonusRedemptionSuccess}
                </Text>
              )}
            </>
          )}

          {sponsorship.videos.data.filter((v) => v.is_valid).length > 0 && (
            <>
              <Text size="sm" fw={600}>
                Submitted Videos
              </Text>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Views</Table.Th>
                    <Table.Th w={40}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sponsorship.videos.data
                    .filter((v) => v.is_valid)
                    .map((video, idx) => (
                      <Table.Tr key={idx}>
                        <Table.Td
                          style={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Text size="sm">{video.description || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {video.views != null
                              ? video.views.toLocaleString()
                              : "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {video.url && (
                            <Anchor
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconExternalLink size={16} />
                            </Anchor>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>

              {sponsorship.videos.data.filter((v) => !v.is_valid).length >
                0 && (
                <>
                  <UnstyledButton onClick={toggleInvalid}>
                    <Group gap={4}>
                      {invalidOpen ? (
                        <IconChevronDown size={14} color="gray" />
                      ) : (
                        <IconChevronRight size={14} color="gray" />
                      )}
                      <Text size="xs" c="dimmed">
                        Invalid videos submitted
                      </Text>
                    </Group>
                  </UnstyledButton>
                  <Collapse in={invalidOpen}>
                    <Stack gap="sm" mt="xs">
                      <Alert
                        icon={<IconAlertTriangle size={18} />}
                        color="red"
                        radius="md"
                      >
                        These videos are invalid because they do not use the
                        correct music. To be valid you must use{" "}
                        <Anchor
                          href="https://www.tiktok.com/music/Wings-7619193296545450000"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          this music
                        </Anchor>
                        .
                      </Alert>
                      <Table striped withTableBorder style={{ opacity: 0.6 }}>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Description</Table.Th>
                            <Table.Th w={40}></Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {sponsorship.videos.data
                            .filter((v) => !v.is_valid)
                            .map((video, idx) => (
                              <Table.Tr key={idx}>
                                <Table.Td
                                  style={{
                                    maxWidth: 180,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Text size="sm">
                                    {video.description || "—"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  {video.url && (
                                    <Anchor
                                      href={video.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <IconExternalLink size={16} />
                                    </Anchor>
                                  )}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                        </Table.Tbody>
                      </Table>
                    </Stack>
                  </Collapse>
                </>
              )}

              {sponsorship.isAllowedToScrape &&
              !sponsorship.hasReachedMaximumViews ? (
                <Button
                  leftSection={<IconRefresh size={18} />}
                  onClick={() => sponsorship.updateVideoViews()}
                  disabled={bob.areWeWaitingOnRails}
                  loading={bob.areWeWaitingOnRails}
                >
                  Update Video Views
                </Button>
              ) : sponsorship.hasReachedMaximumViews ? (
                <>
                  <Text size="sm" c="dimmed">
                    Maximum views reached for this sponsorship.
                  </Text>
                  <Text size="sm" c="dimmed">
                    To increase this maximum, please reply to the last email you
                    received from info@trackonerecordings.com with the request
                    "please increase my sponsorship's maximum views". We will
                    review your request and respond to the email with next
                    steps.
                  </Text>
                  <Text size="sm" c="dimmed">
                    Note: You must directly reply to the existing email thread.
                    Please do not start a new email thread or modify the subject
                    line.
                  </Text>
                </>
              ) : (
                <Text size="sm" c="dimmed">
                  {(() => {
                    if (!sponsorship.nextScrapeAllowedAt) return "—";
                    const diff =
                      new Date(sponsorship.nextScrapeAllowedAt).getTime() -
                      Date.now();
                    if (diff <= 0) return "You can update video views now";
                    const hours = Math.floor(diff / 3600000);
                    const minutes = Math.ceil((diff % 3600000) / 60000);
                    if (hours > 0)
                      return `You can update video views in ${hours}h ${minutes}m`;
                    return `You can update video views in ${minutes}m`;
                  })()}
                </Text>
              )}

              {sponsorship.videoViewsUpdateError && (
                <Text c="red" size="sm">
                  {sponsorship.videoViewsUpdateError}
                </Text>
              )}
              {sponsorship.videoViewsUpdateSuccess && (
                <Text c="green" size="sm">
                  {sponsorship.videoViewsUpdateSuccess}
                </Text>
              )}
            </>
          )}

          {sponsorship.redemptions.data.length > 0 && (
            <>
              <Text size="sm" fw={600}>
                Payouts
              </Text>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Kind</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sponsorship.redemptions.data.map((r, idx) => (
                    <Table.Tr key={idx}>
                      <Table.Td>
                        <Text size="sm">
                          {new Date(r.created_at).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {r.amount_satoshis.toLocaleString()} sats
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{r.kind.replace(/_/g, " ")}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {sponsorship.availableRedemptionSatoshis > 0 && (
                <Button
                  onClick={() => sponsorship.redeemViews()}
                  loading={bob.areWeWaitingOnRails}
                  disabled={bob.areWeWaitingOnRails}
                >
                  Redeem{" "}
                  {sponsorship.availableRedemptionSatoshis.toLocaleString()}{" "}
                  sats
                </Button>
              )}

              {sponsorship.viewRedemptionError && (
                <Text c="red" size="sm">
                  {sponsorship.viewRedemptionError}
                </Text>
              )}
              {sponsorship.viewRedemptionSuccess && (
                <Text c="green" size="sm">
                  {sponsorship.viewRedemptionSuccess}
                </Text>
              )}
            </>
          )}

          {!sponsorship.hasReachedMaximumViews && (
            <>
              <Text size="sm" fw={600}>
                Add a TikTok Video
              </Text>

              {videoProposal && (
                <>
                  <TextInput
                    label="TikTok video URL"
                    placeholder="https://www.tiktok.com/@username/photo/1234567890"
                    autoComplete="off"
                    data-1p-ignore
                    data-bwignore
                    data-lpignore="true"
                    data-form-type="other"
                    value={videoProposal.proposedUrl}
                    onChange={(e) =>
                      videoProposal.setProposedUrl(e.currentTarget.value)
                    }
                    onPaste={(e) => {
                      e.preventDefault();
                      videoProposal.setProposedUrl(
                        e.clipboardData.getData("text"),
                      );
                    }}
                    error={videoProposal.userFacingUrlErrorMessage}
                    disabled={
                      bob.areWeWaitingOnRails || videoProposal.isResolving
                    }
                  />

                  {videoProposal.isResolving && (
                    <Group gap="xs">
                      <Loader size="xs" />
                      <Text size="sm" c="dimmed">
                        Resolving link...
                      </Text>
                    </Group>
                  )}

                  {videoProposal.isUrlReady && (
                    <Text
                      size="sm"
                      c="green"
                      style={{ wordBreak: "break-all" }}
                    >
                      <IconCheck
                        size={14}
                        style={{ verticalAlign: "middle" }}
                      />{" "}
                      {videoProposal.resolvedUrl}
                    </Text>
                  )}

                  {videoProposal.serverErrorMessage && (
                    <Alert
                      icon={<IconAlertTriangle size={18} />}
                      color="red"
                      radius="md"
                    >
                      {videoProposal.serverErrorMessage}
                    </Alert>
                  )}

                  {videoProposal.serverSuccessMessage && (
                    <Alert
                      icon={<IconCheck size={18} />}
                      color="green"
                      radius="md"
                    >
                      {videoProposal.serverSuccessMessage}
                    </Alert>
                  )}

                  {videoProposal.isUrlReady &&
                    !videoProposal.serverErrorMessage && (
                      <Button
                        leftSection={<IconVideo size={18} />}
                        onClick={() => videoProposal.addVideo()}
                        disabled={bob.areWeWaitingOnRails}
                        loading={bob.areWeWaitingOnRails}
                      >
                        Add Video
                      </Button>
                    )}

                  {videoProposal.serverErrorMessage && (
                    <Button
                      variant="outline"
                      color="red"
                      onClick={() => videoProposal.setProposedUrl("")}
                    >
                      Start Over
                    </Button>
                  )}

                  {videoProposal.userFacingUrlErrorMessage &&
                    !videoProposal.serverErrorMessage && (
                      <Button
                        variant="outline"
                        color="red"
                        onClick={() => videoProposal.setProposedUrl("")}
                      >
                        Start Over
                      </Button>
                    )}
                </>
              )}
            </>
          )}

          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              onClick={() => {
                sponsorship.deleteAllVideoProposals();
                sponsorship.setVideoViewsUpdateSuccess(null);
                sponsorship.setVideoViewsUpdateError(null);
                sponsorship.setViewRedemptionSuccess(null);
                sponsorship.setViewRedemptionError(null);
                sponsorship.setBonusRedemptionSuccess(null);
                sponsorship.setBonusRedemptionError(null);
                bob.setOpenModal(null);
              }}
            >
              Close
            </Button>
          </Group>

          <UnstyledButton onClick={toggleInfo}>
            <Group gap={4}>
              {infoOpen ? (
                <IconChevronDown size={14} color="gray" />
              ) : (
                <IconChevronRight size={14} color="gray" />
              )}
              <Text size="xs" c="dimmed">
                Sponsorship info
              </Text>
            </Group>
          </UnstyledButton>
          <Collapse in={infoOpen}>
            <Stack gap="xs" mt="xs">
              <Text size="sm" c="dimmed">
                TikTok Username
              </Text>
              <Text fw={600}>{sponsorship.tokuserName}</Text>

              <Text size="sm" c="dimmed">
                Satoshis Per View
              </Text>
              <Text fw={600}>{sponsorship.satoshisPerView}</Text>

              <Text size="sm" c="dimmed">
                Starting Bonus
              </Text>
              <Text fw={600}>
                {sponsorship.startingBonusSatoshis.toLocaleString()} sats
              </Text>

              <Text size="sm" c="dimmed">
                Maximum Views
              </Text>
              <Text fw={600}>{sponsorship.maximumViews.toLocaleString()}</Text>

              <Text size="sm" c="dimmed">
                Starting Bonus Redemption
              </Text>
              {sponsorship.startingBonusRedeemedAt && (
                <Text fw={600}>
                  Redeemed on{" "}
                  {new Date(
                    sponsorship.startingBonusRedeemedAt,
                  ).toLocaleDateString()}
                </Text>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </Paper>
    </Modal>
  );
});

export default SponsorshipModal;
